import React from "react";
import { cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

const buttonClasses = cva(
  "inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 active:scale-95",
  {
    variants: {
      variant: {
        primary:
          " text-primary-foreground border-primary-background focus:ring-primary-background",
        secondary:
          "bg-secondary-background text-secondary-foreground border-secondary-background focus:ring-secondary-background",
        outline: "bg-transparent hover:bg-opacity-10 focus:ring-blue-500",
      },
      size: {
        small: "text-xs px-4 py-2",
        medium: "text-sm px-6 py-3",
        large: "text-md px-8 py-4",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "medium",
    },
  }
);

const Button = ({
  // Required parameters with defaults
  text = "Contact Us",
  text_font_size = "text-sm",
  text_font_family = "Parkinsans",
  text_font_weight = "font-semibold",
  text_line_height = "leading-normal",
  text_text_align = "center",
  text_color = "text-secondary-text",
  border_border = "1px solid #343163",
  border_border_radius = "rounded-xl",

  // Optional parameters (no defaults)
  layout_width,
  padding,
  position,
  margin,

  // Standard React props
  variant,
  size,
  disabled = false,
  className,
  children,
  onClick,
  type = "button",
  ...props
}) => {
  // Safe validation for optional parameters
  const hasValidWidth =
    layout_width &&
    typeof layout_width === "string" &&
    layout_width?.trim() !== "";
  const hasValidPadding =
    padding && typeof padding === "string" && padding?.trim() !== "";
  const hasValidMargin =
    margin && typeof margin === "string" && margin?.trim() !== "";
  const hasValidPosition =
    position && typeof position === "string" && position?.trim() !== "";

  // Parse padding if provided
  const parsePadding = (paddingStr) => {
    if (!paddingStr) return "";
    const parts = paddingStr?.split(",");
    let classes = [];

    parts?.forEach((part) => {
      const [side, value] = part?.split("=");
      const pixelValue = value?.replace("px", "");
      switch (side) {
        case "t":
          classes?.push(`pt-[${pixelValue}px]`);
          break;
        case "r":
          classes?.push(`pr-[${pixelValue}px]`);
          break;
        case "b":
          classes?.push(`pb-[${pixelValue}px]`);
          break;
        case "l":
          classes?.push(`pl-[${pixelValue}px]`);
          break;
      }
    });
    return classes?.join(" ");
  };

  // Parse margin if provided
  const parseMargin = (marginStr) => {
    if (!marginStr) return "";
    const parts = marginStr?.split(",");
    let classes = [];

    parts?.forEach((part) => {
      const [side, value] = part?.split("=");
      const pixelValue = value?.replace("px", "");
      switch (side) {
        case "t":
          classes?.push(`mt-[${pixelValue}px]`);
          break;
        case "r":
          classes?.push(`mr-[${pixelValue}px]`);
          break;
        case "b":
          classes?.push(`mb-[${pixelValue}px]`);
          break;
        case "l":
          classes?.push(`ml-[${pixelValue}px]`);
          break;
      }
    });
    return classes?.join(" ");
  };

  // Build optional Tailwind classes
  const optionalClasses = [
    hasValidWidth
      ? layout_width === "auto"
        ? "w-auto"
        : `w-[${layout_width}]`
      : "",
    hasValidPadding ? parsePadding(padding) : "px-[32px] py-[12px]",
    hasValidMargin ? parseMargin(margin) : "",
    hasValidPosition ? position : "",
  ]
    ?.filter(Boolean)
    ?.join(" ");

  // Safe click handler
  const handleClick = (event) => {
    if (disabled) return;
    if (typeof onClick === "function") {
      onClick(event);
    }
  };

  // Build dynamic styles
  const dynamicStyles = {
    fontFamily: text_font_family || "Parkinsans",
    textAlign: text_text_align || "center",
    color: text_color === "#343163" ? "var(--text-secondary-text)" : text_color,
    border: border_border || "1px solid #343163",
  };

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={handleClick}
      style={dynamicStyles}
      className={twMerge(
        buttonClasses({ variant, size }),
        text_font_size,
        text_font_weight,
        text_line_height,
        border_border_radius,
        optionalClasses,
        className
      )}
      aria-disabled={disabled}
      {...props}
    >
      {children || text}
    </button>
  );
};

export default Button;
